import axios, { AxiosResponse } from 'axios';

// Create axios instance for CRM API calls
const crmApi = axios.create({
  baseURL: '/api/crm', // Use relative URL to go through Vite proxy
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
crmApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('redex_admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
crmApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid, remove it
      localStorage.removeItem('redex_admin_token');
      // Redirect to login page if not already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Types for CRM data
export interface CRMLead {
  id?: number;
  businessName: string;
  ownerName: string;
  email?: string;
  phone: string;
  employeeCount: number;
  industry: string;
  website?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  companyNotes?: string;
  tags?: string[];
  status?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CRMContact {
  id?: number;
  email: string;
  title?: string;
  department?: string;
  isPrimaryContact?: boolean;
  notes?: string;
  accountId?: number;
  cellPhone?: string;
  firstName: string;
  lastName: string;
  officePhone?: string;
  phone?: string;
  company?: string;
  industry?: string;
  location?: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export type CRMContactCreationRequest = Omit<CRMContact, 'id' | 'createdAt' | 'updatedAt'>;

export interface CRMLeadGenerationRequest {
  query: string;
  industry?: string;
  location?: string;
  companySize?: 'startup' | 'small' | 'medium' | 'large' | 'enterprise';
  priority?: 'high' | 'medium' | 'low';
  includeContacts?: boolean;
  maxResults?: number;
}

export interface CRMLeadGenerationResponse {
  leads: CRMLead[];
  totalTokens: number;
  cost: number;
  processingTime: number;
}

export interface CRMAnalyticsData {
  totalLeads: number;
  qualifiedLeads: number;
  conversionRate: number;
  revenueGenerated: number;
  topIndustries: Array<{ industry: string; count: number }>;
  leadSources: Array<{ source: string; count: number }>;
  leads?: {
    total: number;
    qualified: number;
    bySource: Array<{ source: string; count: number; percentage: number }>;
    byIndustry: Array<{ industry: string; count: number; percentage: number }>;
  };
  revenue?: {
    total: number;
    bySource: Array<{ source: string; amount: number; percentage: number }>;
  };
}

export interface SyncResult {
  success: boolean;
  syncedRecords: number;
  errors: string[];
  timestamp: string;
}

export interface UnifiedDashboardData {
  crm: CRMAnalyticsData;
  redex: {
    totalClients: number;
    activeSites: number;
    monthlyRevenue: number;
    pendingInvoices: number;
  };
  combined: {
    totalLeadsGenerated: number;
    conversionRate: number;
    monthlyRecurringRevenue: number;
    leadQualityScore: number;
  };
}

export class CRMService {
  /**
   * Test CRM API connectivity
   */
  async testConnection(): Promise<boolean> {
    try {
      const response = await crmApi.get('/health');
      return response.data.data.crmConnected;
    } catch (error) {
      console.error('CRM connection test failed:', error);
      return false;
    }
  }

  /**
   * Generate leads using CRM's REV108 lead engine
   */
  async generateLeads(request: CRMLeadGenerationRequest): Promise<CRMLeadGenerationResponse> {
    const response: AxiosResponse<{ success: boolean; data: CRMLeadGenerationResponse }> =
      await crmApi.post('/leads/generate', request);
    return response.data.data;
  }

  /**
   * Get leads from CRM system
   */
  async getLeads(filters?: {
    status?: string;
    industry?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ leads: CRMLead[]; total: number }> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.industry) params.append('industry', filters.industry);
    if (filters?.limit) params.append('limit', filters.limit?.toString() || '50');
    if (filters?.offset) params.append('offset', filters.offset?.toString() || '0');

    const response: AxiosResponse<{ success: boolean; data: { leads: CRMLead[]; total: number } }> =
      await crmApi.get(`/leads?${params.toString()}`);
    return response.data.data;
  }

  /**
   * Create a new lead in CRM system
   */
  async createLead(lead: Omit<CRMLead, 'id' | 'createdAt' | 'updatedAt'>): Promise<CRMLead> {
    const response: AxiosResponse<{ success: boolean; data: CRMLead }> =
      await crmApi.post('/leads', lead);
    return response.data.data;
  }

  /**
   * Get contacts from CRM system
   */
  async getContacts(filters?: {
    accountId?: number;
    industry?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ contacts: CRMContact[]; total: number }> {
    const params = new URLSearchParams();
    if (filters?.accountId) params.append('accountId', filters.accountId.toString());
    if (filters?.industry) params.append('industry', filters.industry);
    if (filters?.limit) params.append('limit', filters.limit?.toString() || '50');
    if (filters?.offset) params.append('offset', filters.offset?.toString() || '0');

    const response: AxiosResponse<{ success: boolean; data: { contacts: CRMContact[]; total: number } }> =
      await crmApi.get(`/contacts?${params.toString()}`);
    return response.data.data;
  }

  /**
   * Create a new contact in CRM system
   */
  async createContact(contact: Omit<CRMContact, 'id' | 'createdAt' | 'updatedAt'>): Promise<CRMContact> {
    const response: AxiosResponse<{ success: boolean; data: CRMContact }> =
      await crmApi.post('/contacts', contact);
    return response.data.data;
  }

  /**
   * Get CRM analytics data
   */
  async getAnalytics(timeframe?: 'week' | 'month' | 'quarter' | 'year'): Promise<CRMAnalyticsData> {
    const params = timeframe ? `?timeframe=${timeframe}` : '';
    const response: AxiosResponse<{ success: boolean; data: CRMAnalyticsData }> =
      await crmApi.get(`/analytics${params}`);
    return response.data.data;
  }

  /**
   * Get unified dashboard data
   */
  async getUnifiedDashboard(): Promise<UnifiedDashboardData> {
    const response: AxiosResponse<{ success: boolean; data: UnifiedDashboardData }> =
      await crmApi.get('/dashboard');
    return response.data.data;
  }

  /**
   * Sync REDEX client to CRM contact
   */
  async syncClientToContact(clientData: {
    name: string;
    email: string;
    phone?: string;
    industry?: string;
    website?: string;
  }): Promise<{ contact: CRMContact; message: string }> {
    const response: AxiosResponse<{ success: boolean; data: { contact: CRMContact; message: string } }> =
      await crmApi.post('/sync/client', clientData);
    return response.data.data;
  }

  /**
   * Get data synchronization status
   */
  async getSyncStatus(): Promise<{
    lastSync: string | null;
    totalClients: number;
    syncedClients: number;
    totalLeads: number;
    syncedLeads: number;
    syncHealth: 'healthy' | 'warning' | 'error';
  }> {
    const response: AxiosResponse<{ success: boolean; data: any }> =
      await crmApi.get('/sync/status');
    return response.data.data;
  }

  /**
   * Sync clients to contacts
   */
  async syncClientsToContacts(dryRun = false): Promise<SyncResult> {
    const response: AxiosResponse<{ success: boolean; data: SyncResult }> =
      await crmApi.post('/sync/clients-to-contacts', { dryRun });
    return response.data.data;
  }

  /**
   * Sync leads to clients
   */
  async syncLeadsToClients(dryRun = false, minScore?: number): Promise<SyncResult> {
    const response: AxiosResponse<{ success: boolean; data: SyncResult }> =
      await crmApi.post('/sync/leads-to-clients', { dryRun, minScore });
    return response.data.data;
  }

  /**
   * Sync contacts to clients (reverse direction)
   */
  async syncContactsToClients(dryRun = false): Promise<SyncResult> {
    const response: AxiosResponse<{ success: boolean; data: SyncResult }> =
      await crmApi.post('/sync/contacts-to-clients', { dryRun });
    return response.data.data;
  }

  /**
   * Run full data synchronization
   */
  async runFullSync(options: {
    dryRun?: boolean;
    syncClientsToContacts?: boolean;
    syncLeadsToClients?: boolean;
    syncInvoicesToQuotes?: boolean;
  } = {}): Promise<{
    clientsToContacts: SyncResult;
    leadsToClients: SyncResult;
    invoicesToQuotes: SyncResult;
  }> {
    const response: AxiosResponse<{ success: boolean; data: any }> =
      await crmApi.post('/sync/full', options);
    return response.data.data;
  }
}

// Export singleton instance
export const crmService = new CRMService();